/* eslint-disable max-len */
import React, { useState } from 'react'
import {
  Drawer,
  DownloadButton,
  BackButton,
  ButtonContainer,
  DataContainer,
  DrawerContainer,
  ErrorContainer,
  ErrorLegend,
  ErrorState,
  HeaderContainer,
  PDFContainer,
  PendingContainer,
  PendingLegend,
  PendingState,
  ReceiptDetailText,
  ToastRenderItem,
  styles,
  LinkHelp,
  OutOfHoursContainer,
  OutOfHoursText,
  CancelBoldText,
  CancelButtonContainer,
  CancelContainer,
  CancelIcon,
  CancelMiddleContent,
  CancelRegularText
} from './style'
import BackArrowIcon from '@qirapagos/web/src/assets/svg/BackArrow'
import { useAppDispatch, useAppSelector } from 'hooks/redux'
import { Drawers } from 'store/drawer/types'
import wrapper from 'components/organisms/Drawers/wrapper'
import { useToast } from '@chakra-ui/react'
import CancelRescue from '@qirapagos/lib/assets/svg/CancelRescue'
import SuccessTickWithBackground from 'assets/svg/SuccessTickWithBackground'
import useSharePdf from 'hooks/useSharePdf'
import CustomList from 'components/molecules/CustomList'
import { formatDate } from '@qirapagos/lib/utils/common'
import { InvestmentOrderType, InvestmentStatus } from 'store/investments/types'
import Button from 'components/atoms/Button'
import { cancelInvestmentOrder } from 'store/investments/thunks'
import { useNavigate } from 'react-router-dom'
import RescuePending from '@qirapagos/lib/assets/svg/RescuePending'
import RescueRejected from '@qirapagos/lib/assets/svg/RescueRejected'
import TransferPending from '@qirapagos/lib/assets/svg/TransferPending'

interface Props {
  showDrawer: boolean
  onClose: () => void
}

const InvestmentDetail: React.FC<Props> = ({ showDrawer, onClose }) => {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const dispatch = useAppDispatch()
  const { selected, investmentHours, isLoading } = useAppSelector(
    state => state.investments
  )
  const { printRef, onShareHandler } = useSharePdf(220, 110)
  const toast = useToast({ containerStyle: { width: '100%' } })
  const navigate = useNavigate()

  const subscriptionHours = investmentHours?.subscription
  const rescueHours = investmentHours?.rescue
  const isSubscription =
    selected?.activityData.type === InvestmentOrderType.SUBSCRIPTION
  const status = selected?.activityData.status

  const statusFlags = {
    notSendedInvera: status === InvestmentStatus.NotSendedInvera,
    pending: [InvestmentStatus.Pending, InvestmentStatus.ManualAction].includes(
      status as InvestmentStatus
    ),
    canceled: status === InvestmentStatus.Canceled,
    rejected: status === InvestmentStatus.Rejected,
    approved: status === InvestmentStatus.Executed
  }
  const titleString = isSubscription ? 'Suscripción' : 'Rescate'

  const data = [
    { Fecha: formatDate(selected?.createdAt.toString() || '') },
    isSubscription
      ? { 'Cuenta crédito': selected?.account }
      : { 'Cuenta débito': selected?.account },
    { Monto: `$${selected?.amount.toLocaleString('es-AR')}` }
  ]

  const getPendingText = () => {
    if (statusFlags.notSendedInvera) {
      const operationText = isSubscription ? 'invertido' : 'rescatado'
      const target = isSubscription ? 'el módulo inversiones' : 'tu billetera'
      return {
        text: `Tu operación está pendiente. En breve, tu dinero será ${operationText}. Te avisaremos cuando esté todo listo y verás reflejado el dinero en ${target}.`
      }
    }
    return {
      text: 'Tu operación está pendiente debido a demoras en el procesamiento. Este tipo de operaciones puede tardar un poco más de lo habitual. Consultanos más tarde.'
    }
  }

  const handleHelp = () => {
    navigate('/content/customer-support')
  }

  const renderError = () => {
    let title = ''
    let text = ''
    title = `${titleString} ${isSubscription ? 'cancelada' : 'cancelado'}`

    text = isSubscription
      ? 'Vemos que cancelaste tu suscripción. Si querés realizar una nueva suscripción, podés hacerlo en cualquier momento desde la App. ¿Tenés dudas o necesitás ayuda? Contactanos haciendo click en el siguiente link.'
      : 'Vemos que cancelaste tu rescate. Si querés realizar un nuevo rescate, podés hacerlo en cualquier momento desde la App. ¿Tenés dudas o necesitás ayuda? Contactanos haciendo click en el siguiente link.'
    if (statusFlags.rejected) {
      title = `${titleString} ${isSubscription ? 'rechazada' : 'rechazado'}`

      text =
        'No pudimos completar tu operación debido a un problema ajeno a nuestra App. Por favor, intentá nuevamente más tarde. Si el inconveniente persiste, contactanos haciendo click en el siguiente link.'
    }
    return (
      <>
        <ErrorContainer>
          <ErrorState>
            <RescueRejected />
            {title}
          </ErrorState>
          <ErrorLegend>{text}</ErrorLegend>
        </ErrorContainer>
        <LinkHelp
          onClick={() => {
            onClose()
            handleHelp()
          }}
        >
          Necesito ayuda
        </LinkHelp>
      </>
    )
  }

  const renderPending = () => (
    <PendingContainer>
      <PendingState>
        {isSubscription ? <TransferPending /> : <RescuePending />}
        {titleString}
        {' '}
        pendiente
      </PendingState>
      <PendingLegend>{getPendingText().text}</PendingLegend>
    </PendingContainer>
  )

  const renderApproved = () => (
    <>
      <OutOfHoursContainer>
        <OutOfHoursText>Las suscripciones pueden realizarse de</OutOfHoursText>
        <OutOfHoursText bold>
          Lunes a Viernes de
          {' '}
          {subscriptionHours?.join(' a ')}
          {' '}
          hs.
        </OutOfHoursText>
        <OutOfHoursText>Los rescates pueden realizarse de</OutOfHoursText>
        <OutOfHoursText bold>
          Lunes a Viernes de
          {' '}
          {rescueHours?.join(' a ')}
          {' '}
          hs.
        </OutOfHoursText>
        <OutOfHoursText>No disponibles en feriados.</OutOfHoursText>
      </OutOfHoursContainer>
      <ButtonContainer>
        <DownloadButton
          onPress={() => {
            toast({
              position: 'bottom',
              render: () => (
                <ToastRenderItem>
                  El comprobante se ha descargardo correctamente
                  <SuccessTickWithBackground />
                </ToastRenderItem>
              )
            })
            onShareHandler()
          }}
        >
          Descargar PDF
        </DownloadButton>
      </ButtonContainer>
    </>
  )

  const renderCancelConfirmation = () => (
    <>
      <HeaderContainer>
        <BackButton
          onClick={() => {
            if (!isLoading) setShowCancelConfirmation(false)
          }}
        >
          <BackArrowIcon />
        </BackButton>
        <ReceiptDetailText>
          Cancelar
          {' '}
          {titleString.toLocaleLowerCase()}
        </ReceiptDetailText>
      </HeaderContainer>

      <CancelContainer>
        <CancelMiddleContent>
          <CancelIcon>
            <CancelRescue />
          </CancelIcon>
          <CancelBoldText>
            ¿Querés cancelar tu
            {' '}
            {titleString.toLocaleLowerCase()}
            ?
          </CancelBoldText>
          <CancelRegularText>
            {`Si confirmás, se cancelará tu solicitud de ${titleString.toLocaleLowerCase()}. Podés iniciar ${isSubscription ? 'una nueva' : 'un nuevo'
              } ${titleString.toLocaleLowerCase()} cuando quieras desde la App.`}
          </CancelRegularText>
        </CancelMiddleContent>

        <CancelButtonContainer>
          <Button
            secondary
            disabled={isLoading}
            onPress={() => {
              if (!isLoading) setShowCancelConfirmation(false)
            }}
          >
            Volver atrás
          </Button>
          <Button
            loading={isLoading}
            onPress={() => {
              selected &&
                dispatch(
                  cancelInvestmentOrder(selected?.id, onClose, isSubscription)
                )
            }}
          >
            Confirmar cancelación
          </Button>
        </CancelButtonContainer>
      </CancelContainer>
    </>
  )

  return (
    <Drawer
      open={showDrawer}
      onClose={() => {
        if (!isLoading) onClose()
      }}
      direction="right"
    >
      <DrawerContainer>
        {showCancelConfirmation
          ? renderCancelConfirmation()
          : (
            <>
              <HeaderContainer>
                <BackButton
                  onClick={() => {
                    if (!isLoading) onClose()
                  }}
                >
                  <BackArrowIcon />
                </BackButton>
                <ReceiptDetailText>
                  Detalle de
                  {' '}
                  {titleString.toLocaleLowerCase()}
                </ReceiptDetailText>
              </HeaderContainer>
              <PDFContainer ref={printRef}>
                {(statusFlags.rejected || statusFlags.canceled) && renderError()}
                {(statusFlags.pending || statusFlags.notSendedInvera) &&
                  renderPending()}
                <DataContainer>
                  <CustomList
                    data={data}
                    positioning="vertical"
                    customStyle={{
                      title: styles.title,
                      flatList: styles.flatList,
                      containerStyle: styles.contentStyle
                    }}
                  />
                </DataContainer>
              </PDFContainer>
              {statusFlags.approved && renderApproved()}
              {statusFlags.notSendedInvera && (
                <ButtonContainer>
                  <DownloadButton onPress={() => setShowCancelConfirmation(true)}>
                    Cancelar
                    {' '}
                    {titleString.toLocaleLowerCase()}
                  </DownloadButton>
                </ButtonContainer>
              )}
            </>
            )}
      </DrawerContainer>
    </Drawer>
  )
}

export default wrapper(InvestmentDetail, Drawers.INVESTMENT_DETAIL)
